import FilterIcon from "@/assets/images/icons/filter_icon.svg";
import { LevelIndicatorBars } from "@/components/ui/LevelIndicatorBars";
import { CourseSkillLevel } from "@/lib/constants";
import { LinearGradient } from "expo-linear-gradient";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dimensions,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  View,
} from "react-native";
import { Button, SizableText, XStack, YStack } from "tamagui";
import {
  SKILL_LEVEL_PANEL_MAX_HEIGHT,
  SKILL_LEVEL_PANEL_WIDTH,
  SKILL_TIERS,
  type TierPickState,
} from "./globalSearchConstants";
import {
  INSTRUMENT_IDS,
  instrumentLabel,
  type InstrumentId,
  type InstrumentPickState,
} from "@/components/courses/courseCatalogFilters";
import { HomeSurveyInstrumentIcon } from "@/components/courses/HomeSurveyInstrumentIcon";
import { dashboardToolbarIconColor } from "@/lib/dashboardToolbarUi";

/** Instruments + Levels stacked make the panel taller; ~20% over the levels-only height, then scroll. */
const FILTER_PANEL_MAX_HEIGHT = Math.round(SKILL_LEVEL_PANEL_MAX_HEIGHT * 1.2);

const FILTER_TRIGGER_BORDER = "rgba(210, 204, 197, 1)";
const FILTER_TRIGGER_ICON = "rgba(92, 88, 84, 1)";

/** Active level chip */
const SKILL_CHIP_BG = "#F4F1ED";
const SKILL_CHIP_BORDER = FILTER_TRIGGER_BORDER;
const SKILL_CHIP_TEXT = "#0D0D0D";

const LEVEL_MENU_SHADOW_WEB = {
  boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.10), 0 0 4px 0 rgba(0, 0, 0, 0.08)",
} as const;

/** Dropdown row: selected bg matches filter chips; bars per Figma */
const LEVEL_MENU_ROW_SELECTED_BG = SKILL_CHIP_BG;
const LEVEL_MENU_BAR_ACTIVE = "rgba(225, 113, 0, 1)";
const LEVEL_MENU_BAR_INACTIVE = "rgba(201, 197, 195, 1)";

const LEVEL_MENU_NATIVE_SHADOW = {
  elevation: 8,
  shadowColor: "#000",
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.1,
  shadowRadius: 8,
} as const;

function SkillLevelMenuPanel({
  isMobile,
  isWeb,
  onClose,
  children,
}: {
  isMobile: boolean;
  isWeb: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <YStack
      bg="white"
      br={8}
      borderWidth={1}
      borderColor="#D2CCC5"
      width={SKILL_LEVEL_PANEL_WIDTH}
      overflow="hidden"
      style={isWeb ? LEVEL_MENU_SHADOW_WEB : LEVEL_MENU_NATIVE_SHADOW}
    >
      {children}
      {isMobile && !isWeb ? (
        <YStack px={24} pb={16} pt={0}>
          <Button
            size="$3"
            bg="#0D0D0D"
            color="white"
            borderRadius={8}
            onPress={onClose}
          >
            Done
          </Button>
        </YStack>
      ) : null}
    </YStack>
  );
}

export function computeSkillLevelsForRpc(tierPick: TierPickState): CourseSkillLevel[] | null {
  const selected = SKILL_TIERS.filter((t) => tierPick[t]);
  if (selected.length === 0 || selected.length === SKILL_TIERS.length) return null;
  return selected;
}

function skillTierLabel(tier: CourseSkillLevel): string {
  switch (tier) {
    case CourseSkillLevel.Beginner:
      return "Beginner";
    case CourseSkillLevel.Intermediate:
      return "Intermediate";
    default:
      return "Advanced";
  }
}

function FilterSlidersIcon({ color = FILTER_TRIGGER_ICON }: { color?: string }) {
  return (
    <FilterIcon
      width={20}
      height={20}
      color={color}
    />
  );
}

function SkillLevelMenuRow({
  tier,
  label,
  selected,
  onToggle,
}: {
  tier: CourseSkillLevel;
  label: string;
  selected: boolean;
  onToggle: () => void;
}) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable onPress={onToggle}>
      <XStack
        ai="center"
        jc="flex-start"
        gap={4}
        py={4}
        px={10}
        br={999}
        borderWidth={1}
        borderColor={FILTER_TRIGGER_BORDER}
        backgroundColor={selected ? LEVEL_MENU_ROW_SELECTED_BG : "#FFFFFF"}
      >
        <SizableText
          flex={1}
          fontSize={14}
          lineHeight={20}
          fontWeight="500"
          color="#0D0D0D"
          textTransform="capitalize"
          letterSpacing={0}
        >
          {label}
        </SizableText>
        <LevelIndicatorBars
          level={tier}
          activeColor={LEVEL_MENU_BAR_ACTIVE}
          inactiveColor={LEVEL_MENU_BAR_INACTIVE}
          size={14}
        />
      </XStack>
    </Pressable>
  );
}

function InstrumentMenuRow({
  id,
  label,
  selected,
  onToggle,
}: {
  id: InstrumentId;
  label: string;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <Pressable onPress={onToggle}>
      <XStack
        ai="center"
        jc="flex-start"
        gap={4}
        py={4}
        px={10}
        br={999}
        borderWidth={1}
        borderColor={FILTER_TRIGGER_BORDER}
        backgroundColor={selected ? LEVEL_MENU_ROW_SELECTED_BG : "#FFFFFF"}
      >
        <SizableText
          flex={1}
          fontSize={14}
          lineHeight={20}
          fontWeight="500"
          color="#0D0D0D"
          textTransform="capitalize"
          letterSpacing={0}
        >
          {label}
        </SizableText>
        <HomeSurveyInstrumentIcon
          id={id}
          size={18}
          color={dashboardToolbarIconColor(selected)}
        />
      </XStack>
    </Pressable>
  );
}

function SkillLevelActiveChip({
  label,
  onRemove,
  onOpenMenu,
}: {
  label: string;
  onRemove: () => void;
  onOpenMenu: () => void;
}) {
  const isWeb = Platform.OS === "web";
  return (
    <XStack
      height={40}
      px={10}
      py={4}
      gap={4}
      ai="center"
      jc="center"
      br={20}
      borderWidth={1}
      borderColor={SKILL_CHIP_BORDER}
      backgroundColor={SKILL_CHIP_BG}
      flexShrink={0}
    >
      <Pressable onPress={onOpenMenu} accessibilityRole="button">
        <SizableText
          fontSize={14}
          lineHeight={20}
          fontWeight="500"
          color={SKILL_CHIP_TEXT}
          textTransform="capitalize"
        >
          {label}
        </SizableText>
      </Pressable>
      <Pressable
        onPress={onRemove}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityLabel={`Remove ${label} filter`}
        accessibilityRole="button"
        style={isWeb ? ({ cursor: "pointer" } as const) : undefined}
      >
        <SizableText fontSize={16} lineHeight={20} fontWeight="500" color={SKILL_CHIP_TEXT}>
          ×
        </SizableText>
      </Pressable>
    </XStack>
  );
}

function LevelsFilterTriggerButton({
  onPress,
  accessibilityLabel,
}: {
  onPress: () => void;
  accessibilityLabel: string;
}) {
  return (
    <YStack position="relative" width={40} height={40}>
      <Button
        unstyled
        width={40}
        height={40}
        br={999}
        borderWidth={1}
        borderColor={FILTER_TRIGGER_BORDER}
        bg="white"
        onPress={onPress}
        pressStyle={{ opacity: 0.88 }}
        hoverStyle={{ backgroundColor: "#FAF9F8" }}
        ai="center"
        jc="center"
        cursor={Platform.OS === "web" ? "pointer" : undefined}
        accessibilityLabel={accessibilityLabel}
      >
        <FilterSlidersIcon />
      </Button>
    </YStack>
  );
}

type SkillLevelFilterToolbarProps = {
  isMobile: boolean;
  tierPick: TierPickState;
  setTierPick: React.Dispatch<React.SetStateAction<TierPickState>>;
  skillMenuOpen: boolean;
  setSkillMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactNode;
  /** Renders before the level trigger (e.g. search field on /courses). */
  leading?: React.ReactNode;
  /** When provided, an INSTRUMENTS section is shown above LEVELS in the dropdown. */
  instrumentPick?: InstrumentPickState;
  setInstrumentPick?: React.Dispatch<React.SetStateAction<InstrumentPickState>>;
};

export function SkillLevelFilterToolbar({
  isMobile,
  tierPick,
  setTierPick,
  skillMenuOpen,
  setSkillMenuOpen,
  children,
  leading,
  instrumentPick,
  setInstrumentPick,
}: SkillLevelFilterToolbarProps) {
  const isWeb = Platform.OS === "web";
  const hasInstruments = !!instrumentPick && !!setInstrumentPick;
  const useNativeModalMenu = isMobile && !isWeb;
  const triggerRef = useRef<View>(null);
  const [menuAnchor, setMenuAnchor] = useState<
    { top: number; left?: number; right?: number } | null
  >(null);

  const updateMenuAnchor = useCallback(() => {
    triggerRef.current?.measureInWindow((x, y, width, height) => {
      const screenWidth = Dimensions.get("window").width;
      setMenuAnchor({
        top: y + height + 6,
        ...(leading
          ? { right: Math.max(16, screenWidth - (x + width)) }
          : { left: Math.max(16, x) }),
      });
    });
  }, [leading]);

  useEffect(() => {
    if (!skillMenuOpen || !useNativeModalMenu) {
      setMenuAnchor(null);
      return;
    }
    const frame = requestAnimationFrame(updateMenuAnchor);
    return () => cancelAnimationFrame(frame);
  }, [skillMenuOpen, useNativeModalMenu, updateMenuAnchor]);

  const toggleSkillMenu = useCallback(() => {
    setSkillMenuOpen((open) => !open);
  }, [setSkillMenuOpen]);

  const openSkillMenu = useCallback(() => {
    setSkillMenuOpen(true);
  }, [setSkillMenuOpen]);

  const closeSkillMenu = useCallback(() => {
    setSkillMenuOpen(false);
  }, [setSkillMenuOpen]);

  const skillFilterRows = useMemo(
    () => (
      <YStack gap={16}>
        {SKILL_TIERS.map((t) => (
          <SkillLevelMenuRow
            key={t}
            tier={t}
            label={skillTierLabel(t)}
            selected={tierPick[t]}
            onToggle={() => setTierPick((p) => ({ ...p, [t]: !p[t] }))}
          />
        ))}
      </YStack>
    ),
    [tierPick, setTierPick],
  );

  const instrumentFilterRows = useMemo(() => {
    if (!hasInstruments) return null;
    return (
      <YStack gap={16}>
        {INSTRUMENT_IDS.map((id) => (
          <InstrumentMenuRow
            key={id}
            id={id}
            label={instrumentLabel(id)}
            selected={!!instrumentPick![id]}
            onToggle={() =>
              setInstrumentPick!((p) => ({ ...p, [id]: !p[id] }))
            }
          />
        ))}
      </YStack>
    );
  }, [hasInstruments, instrumentPick, setInstrumentPick]);

  // INSTRUMENTS sits above LEVELS; the whole body scrolls within the panel when both are shown.
  const skillFilterMenuContent = (
    <ScrollView
      showsVerticalScrollIndicator={hasInstruments}
      style={hasInstruments ? { maxHeight: FILTER_PANEL_MAX_HEIGHT } : undefined}
      contentContainerStyle={{ paddingBottom: 4 }}
    >
      {hasInstruments ? (
        <YStack px={24} pt={24} pb={8}>
          <SizableText
            fontSize={11}
            fontWeight="700"
            letterSpacing={1.2}
            color="#0D0D0D"
            mb={24}
          >
            INSTRUMENTS
          </SizableText>
          {instrumentFilterRows}
        </YStack>
      ) : null}
      <YStack px={24} pt={hasInstruments ? 16 : 24} pb={16}>
        <SizableText
          fontSize={11}
          fontWeight="700"
          letterSpacing={1.2}
          color="#0D0D0D"
          mb={24}
        >
          LEVELS
        </SizableText>
        {skillFilterRows}
      </YStack>
    </ScrollView>
  );

  type ActiveChip = { key: string; label: string; onRemove: () => void };
  const activeChips: ActiveChip[] = [
    ...(hasInstruments
      ? INSTRUMENT_IDS.filter((id) => instrumentPick![id]).map((id) => ({
          key: `instr-${id}`,
          label: instrumentLabel(id),
          onRemove: () =>
            setInstrumentPick!((p) => ({ ...p, [id]: false })),
        }))
      : []),
    ...SKILL_TIERS.filter((t) => tierPick[t]).map((t) => ({
      key: `tier-${t}`,
      label: skillTierLabel(t),
      onRemove: () => setTierPick((p) => ({ ...p, [t]: false })),
    })),
  ];

  const activeChipRow =
    activeChips.length > 0 ? (
      <XStack gap={8} ai="center" flexShrink={0}>
        {activeChips.map((chip) => (
          <SkillLevelActiveChip
            key={chip.key}
            label={chip.label}
            onRemove={chip.onRemove}
            onOpenMenu={openSkillMenu}
          />
        ))}
      </XStack>
    ) : null;

  const scrollContentStyle = {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 16,
  };

  const scrollInner = (
    <>
      {activeChipRow}
      {children}
    </>
  );

  const levelMenuPanel = (
    <SkillLevelMenuPanel
      isMobile={isMobile}
      isWeb={isWeb}
      onClose={closeSkillMenu}
    >
      {skillFilterMenuContent}
    </SkillLevelMenuPanel>
  );

  const inlineLevelMenu =
    skillMenuOpen && !useNativeModalMenu ? (
      <>
        {isWeb ? (
          <Pressable
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 260,
            }}
            onPress={closeSkillMenu}
          />
        ) : null}
        <YStack
          position="absolute"
          left={leading ? undefined : 0}
          right={leading ? 0 : undefined}
          top="100%"
          mt={6}
          zIndex={280}
        >
          {levelMenuPanel}
        </YStack>
      </>
    ) : null;

  const nativeLevelMenuModal =
    skillMenuOpen && useNativeModalMenu && menuAnchor ? (
      <Modal
        visible
        transparent
        animationType="fade"
        onRequestClose={closeSkillMenu}
        statusBarTranslucent
      >
        <View style={{ flex: 1 }}>
          <Pressable style={{ flex: 1 }} onPress={closeSkillMenu} />
          <View
            pointerEvents="box-none"
            style={{
              position: "absolute",
              top: menuAnchor.top,
              left: menuAnchor.left,
              right: menuAnchor.right,
            }}
          >
            {levelMenuPanel}
          </View>
        </View>
      </Modal>
    ) : null;

  const levelTrigger = (
    <View ref={triggerRef} collapsable={false} style={{ flexShrink: 0 }}>
      <YStack position="relative" zIndex={isMobile ? 120 : 50} flexShrink={0}>
        <LevelsFilterTriggerButton
          onPress={toggleSkillMenu}
          accessibilityLabel="Filter results by instrument and skill level"
        />
        {inlineLevelMenu}
      </YStack>
    </View>
  );

  if (leading) {
    return (
      <>
        {nativeLevelMenuModal}
        <YStack gap={activeChipRow ? 8 : 0} width="100%" maxWidth="100%">
        <XStack
          ai="center"
          gap={12}
          width="100%"
          maxWidth="100%"
          zIndex={2}
        >
          <XStack
            f={isMobile ? 1 : undefined}
            flexGrow={isMobile ? 1 : 0}
            flexShrink={1}
            minWidth={0}
            ai="center"
          >
            {leading}
          </XStack>
          {levelTrigger}
        </XStack>
        {activeChipRow ? (
          isWeb ? (
            <XStack width="100%" zIndex={1}>
              {activeChipRow}
            </XStack>
          ) : (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ alignItems: "center", gap: 8 }}
            >
              {activeChips.map((chip) => (
                <SkillLevelActiveChip
                  key={chip.key}
                  label={chip.label}
                  onRemove={chip.onRemove}
                  onOpenMenu={openSkillMenu}
                />
              ))}
            </ScrollView>
          )
        ) : null}
        </YStack>
      </>
    );
  }

  return (
    <>
      {nativeLevelMenuModal}
      <XStack mt="$3" ai="center" gap={16} width="100%" maxWidth="100%">
      {levelTrigger}

      <XStack f={1} minWidth={0} overflow="hidden" ai="center">
        {isWeb ? (
          <View
            style={{
              flex: 1,
              minWidth: 0,
              maxWidth: "100%",
              position: "relative",
            }}
          >
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              showsVerticalScrollIndicator={false}
              style={{ flex: 1, minWidth: 0, maxWidth: "100%" }}
              contentContainerStyle={scrollContentStyle}
            >
              {scrollInner}
            </ScrollView>
            <LinearGradient
              pointerEvents="none"
              colors={["rgba(255,255,255,0)", "rgba(255,255,255,1)"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 32,
              }}
            />
          </View>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={{ flexGrow: 1, minWidth: 0 }}
            contentContainerStyle={scrollContentStyle}
          >
            {scrollInner}
          </ScrollView>
        )}
      </XStack>
    </XStack>
    </>
  );
}
